import './button-style.scss';

import { Slot, Slottable } from '@radix-ui/react-slot';
import type { ReactNode } from 'react';

import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';

import { cn } from '../../utils/helpers.ts';

export type ButtonStyleProps = React.PropsWithChildren<{
  'aria-disabled'?: boolean;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  iconAfter?: ReactNode;
  iconBefore?: ReactNode;
  isBusy?: boolean;
  purpose?: 'positive' | 'neutral' | 'warning' | 'negative' | 'disabled';
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  textwrap?: boolean;
  variant?: 'primary' | 'secondary' | 'tertiary';
}>;

/**
 * HOC(ish) that is responsible for applying design system button styling directly to its child.
 * It is only concerned with adding the necessary classes to whatever child is passed to it,
 * and is agnostic as to what kind of HTML element is being rendered.
 */
export const ButtonStyle: React.FC<
  ButtonStyleProps & { ref?: React.Ref<HTMLDivElement> }
> = ({
  className,
  disabled,
  fullWidth,
  iconBefore,
  iconAfter,
  isBusy,
  purpose,
  size = 'lg',
  textwrap = false,
  variant = 'primary',
  children,
  ref,
  ...props
}) => {
  return (
    <Slot
      ref={ref}
      {...props}
      // Hack to make TypeScript happy
      {...((isBusy || disabled) && {
        disabled: true,
      })}
      // Overwrite className and style to prevent anyone altering styles.
      className={cn(
        `ButtonStyle ButtonStyle--${variant} ButtonStyle--${size} gap-snug-xl flex justify-center items-center`,
        {
          'ButtonStyle--wrap': textwrap,
          'ButtonStyle--fullWidth': fullWidth,
          'ButtonStyle--positive': purpose === 'positive',
          'ButtonStyle--neutral': purpose === 'neutral',
          'ButtonStyle--warning': purpose === 'warning',
          'ButtonStyle--negative': purpose === 'negative',
          'ButtonStyle--disabled':
            purpose === 'disabled' ||
            disabled ||
            props['aria-disabled'] ||
            isBusy,
          'ButtonStyle--busy': isBusy,
        },
        className,
      )}
      style={undefined}
    >
      {!iconBefore && !iconAfter && isBusy && (
        <ArkIcon iconName="loading-02" className="Icon--loading" />
      )}
      {iconBefore &&
        (isBusy ? (
          <ArkIcon iconName="loading-02" className="Icon--loading" />
        ) : (
          iconBefore
        ))}
      <Slottable>{children}</Slottable>
      {iconAfter &&
        (isBusy && !iconBefore ? (
          <ArkIcon iconName="loading-02" className="Icon--loading" />
        ) : (
          iconAfter
        ))}
    </Slot>
  );
};
